import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Menu, TextField } from "@mui/material";
import type { DateTime } from "luxon";
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { ChangesSource, NotificationType, type NurseFullRole, type OpenShiftRequest, type ShiftRequest } from "../../../utils/models";
import { useMobile } from "../../../utils/utilFunctions";
import { NormalizedRequestApprovalDialog } from "../../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { MessageWorkingFilterEnum } from "../../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { useDeleteShift, useUpdateNursesNeeded } from "../hooks";
import { useUpdateRequestTime } from "../hooks/useUpdateRequestTime";

type Handlers = {
	onDelete: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onBroadcast: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onEditNeededNurses: (event: MouseEvent<HTMLElement>, request: OpenShiftRequest) => void;
	onShowAll: (request: ShiftRequest) => void;
	onTimeChange: (request: ShiftRequest, startTime: import("luxon").DateTime, endTime: import("luxon").DateTime) => Promise<void>;
};

type UseRequestItemActionsResult = {
	handlers: Handlers;
	dialogs: ReactNode;
};

export const useRequestItemActions = (): UseRequestItemActionsResult => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isMobile } = useMobile();
	const { chosenDepartment } = useHospitalData();
	const { handleRequestTimeChange } = useUpdateRequestTime();
	const { mutateAsync: deleteShift, isPending: isDeletingShift } = useDeleteShift();
	const { track } = useAnalytics();

	const [deleteRequestConfirmation, setDeleteRequestConfirmation] = useState(false);
	const [requestToDelete, setRequestToDelete] = useState<ShiftRequest>();

	const [showBroadcastMessagePopup, setShowBroadcastMessagePopup] = useState(false);
	const [requestForBroadcast, setRequestForBroadcast] = useState<ShiftRequest>();

	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const [requestToEdit, setRequestToEdit] = useState<OpenShiftRequest>();
	const [editingNeededNurses, setEditingNeededNurses] = useState<number>(1);

	const [isRequestApprovalDialogOpen, setIsRequestApprovalDialogOpen] = useState(false);
	const [requestForApproval, setRequestForApproval] = useState<ShiftRequest>();

	const onDelete = useCallback((event: MouseEvent<HTMLElement>, request: ShiftRequest): void => {
		event.stopPropagation();
		setRequestToDelete(request);
		setDeleteRequestConfirmation(true);
	}, []);

	const handleConfirmDelete = async (): Promise<void> => {
		if (!requestToDelete) return;
		await deleteShift({ shiftId: requestToDelete.id });
		setDeleteRequestConfirmation(false);
		setRequestToDelete(undefined);
	};

	const onBroadcast = useCallback((event: MouseEvent<HTMLElement>, request: ShiftRequest): void => {
		event.stopPropagation();
		setRequestForBroadcast(request);
		setShowBroadcastMessagePopup(true);
	}, []);

	const toggleShowBroadcastMessagePopup = (): void => {
		setShowBroadcastMessagePopup((prev) => !prev);
		if (showBroadcastMessagePopup) setRequestForBroadcast(undefined);
	};

	const handleEditNeededNursesClose = useCallback((): void => {
		setAnchorEl(undefined);
		setRequestToEdit(undefined);
	}, []);

	const { mutateAsync: updateNeededNurses, isPending: isUpdatingNeededNurses } = useUpdateNursesNeeded({ handleClose: handleEditNeededNursesClose });

	const onEditNeededNurses = useCallback((event: MouseEvent<HTMLElement>, request: OpenShiftRequest): void => {
		event.stopPropagation();
		setEditingNeededNurses(request.needed_nurses);
		setRequestToEdit(request);
		setAnchorEl(event.currentTarget);
	}, []);

	const handleSaveNeededNurses = async (): Promise<void> => {
		if (!requestToEdit) return;
		await updateNeededNurses({ shiftId: requestToEdit.id, neededNurses: editingNeededNurses });
	};

	const onShowAll = useCallback(
		(request: ShiftRequest): void => {
			if (isMobile) {
				track("click_request_mobile", { request_id: request.id });
				const path = chosenDepartment?.department_id ? `/${chosenDepartment.department_id}/request/${request.id}` : `/request/${request.id}`;
				navigate(path);
			} else {
				setRequestForApproval(request);
				setIsRequestApprovalDialogOpen(true);
			}
		},
		[isMobile, chosenDepartment?.department_id, navigate, track],
	);

	const handleCloseApprovalDialog = (): void => {
		setIsRequestApprovalDialogOpen(false);
		setRequestForApproval(undefined);
	};

	const onTimeChange = useCallback(
		async (request: ShiftRequest, startTime: DateTime, endTime: DateTime): Promise<void> => {
			await handleRequestTimeChange(request, startTime, endTime, ChangesSource.Requests);
		},
		[handleRequestTimeChange],
	);

	const handlers = useMemo<Handlers>(
		() => ({ onDelete, onBroadcast, onEditNeededNurses, onShowAll, onTimeChange }),
		[onDelete, onBroadcast, onEditNeededNurses, onShowAll, onTimeChange],
	);

	const dialogs = (
		<>
			{showBroadcastMessagePopup && requestForBroadcast && (
				<NewBroadcastMessageModal
					initialSmsMessage={getShiftPickupMessage(
						requestForBroadcast.message.date,
						requestForBroadcast.message.shift_start,
						requestForBroadcast.message.shift_end,
					)}
					addAppLink={true}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
					nurseIdsToIgnore={requestForBroadcast.message.request_data.map((rd) => rd.nurse_id)}
					jobId={requestForBroadcast.job_id}
					onClose={toggleShowBroadcastMessagePopup}
					initialRolesToSelect={requestForBroadcast.message.roles as NurseFullRole[]}
					initialFilters={[
						{
							workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
							dateFilter: requestForBroadcast.message.date,
							timeFilter: {
								start: requestForBroadcast.message.shift_start,
								end: requestForBroadcast.message.shift_end,
							},
						},
					]}
				/>
			)}

			{isRequestApprovalDialogOpen && requestForApproval && (
				<NormalizedRequestApprovalDialog open={isRequestApprovalDialogOpen} request={requestForApproval} onClose={handleCloseApprovalDialog} />
			)}

			<Menu
				data-testid="request-edit-needed-nurses-menu"
				anchorEl={anchorEl}
				open={!!anchorEl}
				onClose={handleEditNeededNursesClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				sx={{ "& .MuiPaper-root": { p: 2 } }}
			>
				<TextField
					data-testid="request-edit-needed-nurses-input"
					type="number"
					value={editingNeededNurses}
					label="Staff needed"
					onChange={(e) => {
						const value = Number.parseInt(e.target.value, 10);
						if (!Number.isNaN(value) && value > 0) setEditingNeededNurses(value);
					}}
					slotProps={{ htmlInput: { min: 1 } }}
					size="small"
					sx={{ width: "100%" }}
				/>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
					<SystemButton
						data-testid="request-edit-needed-nurses-save-button"
						onClick={handleSaveNeededNurses}
						loading={isUpdatingNeededNurses}
						disabled={isUpdatingNeededNurses}
						sx={{ width: "100%" }}
					>
						Save
					</SystemButton>
				</Box>
			</Menu>

			<Dialog data-testid="request-delete-dialog" open={deleteRequestConfirmation} onClose={() => setDeleteRequestConfirmation(false)}>
				<DialogTitle>{t(requestToDelete?.request_type === NotificationType.ReplaceShift ? "are.you.sure" : "delete.open.shift.title")}</DialogTitle>
				<DialogContent>
					{t(requestToDelete?.request_type === NotificationType.ReplaceShift ? "delete.replace.warning" : "delete.open.shift.warning")}
				</DialogContent>
				<DialogActions>
					<SystemButton
						data-testid="request-delete-cancel-button"
						buttonVariant="secondary"
						onClick={() => setDeleteRequestConfirmation(false)}
						disabled={isDeletingShift}
					>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="request-delete-confirm-button"
						buttonVariant="delete"
						onClick={handleConfirmDelete}
						disabled={isDeletingShift}
						loading={isDeletingShift}
					>
						{t("delete.request")}
					</SystemButton>
				</DialogActions>
			</Dialog>
		</>
	);

	return { handlers, dialogs };
};
